import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCredits } from '../context/CreditContext';
import { MOTION_MODELS } from '../constants/models';
import * as api from '../services/api';
import Icons from '../components/Icons';
import LoadingOverlay from '../components/LoadingOverlay';
import Lightbox from '../components/Lightbox';
import MediaTrimmer from '../components/MediaTrimmer';
import { groupItemsByDate } from '../utils/grouping';
import toast from 'react-hot-toast';
import { compressImage } from '../utils/imageCompression';
import { downloadFile } from '../utils/downloadFile';
import MediaLibraryModal from '../components/MediaLibraryModal';
import { confirmAlert } from '../utils/confirmAlert';

export default function MotionSyncPage({ onShowAuth }) {
  const { user, token } = useAuth();
  const { credits, setCredits, items, refreshItems, removeItem, pendingJobs, setPendingJobs, plan } = useCredits();
  const navigate = useNavigate();

  const [model, setModel] = useState('kling-3.0');
  const [showMobilePreview, setShowMobilePreview] = useState(false);
  const [showModelPicker, setShowModelPicker] = useState(false);
  const [loading, setLoading] = useState(false);
  const [cd, setCd] = useState(0);
  const cdRef = useRef();
  const genLockRef = useRef(false);

  const [subjectFile, setSubjectFile] = useState(null);
  const [subjectPreview, setSubjectPreview] = useState(null);
  const [motionFile, setMotionFile] = useState(null);
  const [motionPreviewUrl, setMotionPreviewUrl] = useState(null);
  const [prompt, setPrompt] = useState('');
  const [keepSound, setKeepSound] = useState(true);
  const [orientation, setOrientation] = useState('exact');
  const [mode, setMode] = useState('720p');
  const [motionTrim, setMotionTrim] = useState({ start: 0, end: 30 });
  const [showVideoTrimmer, setShowVideoTrimmer] = useState(false);
  const [tempFile, setTempFile] = useState(null);
  const [resultUrl, setResultUrl] = useState(null);
  const [lbItem, setLbItem] = useState(null);
  const [libOpen, setLibOpen] = useState(false);
  const [libTarget, setLibTarget] = useState(null); // 'image' or 'video'

  const subjectRef = useRef();
  const motionRef = useRef();
  const currentModel = MOTION_MODELS.find(m => m.id === model);
  const durationSec = Math.max(1, Math.ceil(motionTrim.end - motionTrim.start));
  
  let cost = 0;
  if (model === 'kling-2.6') {
    cost = mode === '1080p' ? durationSec * 40 : durationSec * 25;
  } else if (model === 'kling-3.0' || model === 'kling-3') {
    cost = mode === '1080p' ? durationSec * 50 : durationSec * 35;
  } else {
    const baseCost = currentModel?.baseCost || 15;
    cost = mode === '1080p' ? Math.ceil(baseCost * 1.5) : baseCost;
  }

  const startCD = (s) => {
    setCd(s);
    if (cdRef.current) clearInterval(cdRef.current);
    cdRef.current = setInterval(() => {
      setCd(p => { if (p <= 1) { clearInterval(cdRef.current); return 0; } return p - 1; });
    }, 1000);
  };

  const isModelLocked = useCallback((id) => {
    return false;
  }, [plan]);

  useEffect(() => {
    if (isModelLocked(model)) {
      const fallback = MOTION_MODELS.find(m => !isModelLocked(m.id))?.id || MOTION_MODELS[0].id;
      setModel(fallback);
    }
  }, [plan, model, isModelLocked]);

  const handleSubjectUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const compressed = await compressImage(file);
    setSubjectFile(compressed);
    setSubjectPreview(URL.createObjectURL(compressed));
  };

  const handleModelChange = (newModel) => {
    if (isModelLocked(newModel)) {
      setShowModelPicker(false);
      navigate('/subscription');
      return;
    }
    setModel(newModel);
    setShowModelPicker(false);
  };

  const handleMotionUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 100 * 1024 * 1024) { toast.error('File exceeds 100MB limit'); return; }
    // Always open trimmer so user can select exact portion
    setTempFile(file);
    setShowVideoTrimmer(true);
  };

  const handleGen = useCallback(async () => {
    if (genLockRef.current) return;
    if (!user) { if (onShowAuth) onShowAuth(); return; }
    if (cd > 0) { toast.error(`Wait ${cd}s`); return; }
    if (!subjectFile) { toast.error('Upload a subject image'); return; }
    if (!motionFile) { toast.error('Upload a motion clip'); return; }
    if (credits < cost) { toast.error(`Need ${cost} credits`); navigate('/subscription'); return; }
    
    if (isModelLocked(model)) {
      navigate('/subscription');
      return;
    }

    genLockRef.current = true;

    // Create pending job placeholder
    const jobId = `mspend_${Date.now()}`;
    setPendingJobs(prev => [{ id: jobId, prompt: prompt || 'Motion Sync', model: currentModel?.label || model, type: 'motion-sync' }, ...prev]);
    setLoading(true);
    try {
      const result = await api.generateMotionSync(token, {
        model,
        subjectImage: subjectFile?.fileUrl || subjectFile,
        motionVideo: motionFile?.fileUrl || motionFile,
        prompt: prompt || '',
        mode,
        characterOrientation: orientation,
        keepSound,
        trimStart: motionTrim.start,
        trimEnd: motionTrim.end,
      });

      setPendingJobs(prev => prev.filter(j => j.id !== jobId));
      if (result.success) {
        toast.success('Motion Sync complete!');
        if (result.videoUrl) setResultUrl(result.videoUrl);
        setCredits(result.creditsRemaining);
      }
      startCD(12);
      refreshItems(true);
    } catch (e) {
      setPendingJobs(prev => prev.filter(j => j.id !== jobId));
      toast.error(e.response?.data?.error || 'Motion Sync failed. Credits refunded.');
    } finally {
      genLockRef.current = false;
      setLoading(false);
    }
  }, [user, cd, subjectFile, motionFile, prompt, mode, orientation, keepSound, credits, cost, model, token, navigate, onShowAuth, refreshItems, setCredits, currentModel]);

  const handleDeleteItem = async (id) => {
    const isConfirmed = await confirmAlert({
      title: 'Delete Creation?',
      message: 'Are you sure you want to delete this creation? This action cannot be undone.'
    });
    if (!isConfirmed) return;
    try { await api.deleteGeneration(token, id); removeItem(id); toast.success('Deleted'); }
    catch { toast.error('Failed to delete'); }
  };

  const recentVideos = items
    .filter(i => i.subType === 'motion-sync')
    .sort((a, b) => {
      const ta = a.createdAt?.seconds || a.createdAt?._seconds || 0;
      const tb = b.createdAt?.seconds || b.createdAt?._seconds || 0;
      return tb - ta;
    }).slice(0, 12);

  const localJobs = pendingJobs.filter(j => j.type === 'motion-sync');

  return (
    <>
      {loading && <LoadingOverlay type="video" />}
      {showVideoTrimmer && <MediaTrimmer file={tempFile} type="video" maxDuration={30}
        onSave={(s, e) => {
          setMotionTrim({start:s,end:e});
          setMotionFile(tempFile);
          // Create a preview URL with #t= fragment so only trimmed portion plays
          if (motionPreviewUrl && motionPreviewUrl.startsWith('blob:')) URL.revokeObjectURL(motionPreviewUrl.split('#')[0]);
          
          let baseObjUrl = '';
          if (tempFile instanceof Blob || tempFile instanceof File) {
             baseObjUrl = URL.createObjectURL(tempFile);
          } else {
             baseObjUrl = tempFile.fileUrl || tempFile.url;
          }

          setMotionPreviewUrl(`${baseObjUrl}#t=${s.toFixed(2)},${e.toFixed(2)}`);
          setShowVideoTrimmer(false);
          setTempFile(null);
        }}
        onCancel={() => { setShowVideoTrimmer(false); setTempFile(null); }} />}

      {/* ── Model Picker Popup ── */}
      {showModelPicker && (
        <div className="mp-overlay" onClick={() => setShowModelPicker(false)}>
          <div className="mp-popup" onClick={e => e.stopPropagation()}>
            <div className="mp-header">
              <div className="mp-title">Select Model</div>
              <button className="mc-btn" onClick={() => setShowModelPicker(false)}><Icons.X sz={14} /></button>
            </div>
            <div className="mp-body">
              {MOTION_MODELS.map(m => {
                const locked = isModelLocked(m.id);
                return (
                <button key={m.id} className={`mp-item ${model === m.id ? 'on' : ''}`}
                  onClick={() => handleModelChange(m.id)}>
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'center', width: 20, height: 20, marginRight: 10, color: m.color }}>
                    <Icons.Kling sz={18} color={m.color} />
                  </div>
                  <div className="mp-info">
                    <div className="mp-name">
                      {m.label}
                    </div>
                  </div>
                  <div className="mp-badges">
                    {locked && <span className="mp-badge" style={{background: 'rgba(239,68,68,0.15)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.3)'}}>Premium</span>}
                  </div>
                  {locked ? (
                    <div className="mp-lock">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>
                    </div>
                  ) : model === m.id && (
                    <div className="mp-check">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                    </div>
                  )}
                </button>
              )})}
            </div>
          </div>
        </div>
      )}

      {/* Mobile FAB */}
      <button className={`mobile-fab ${showMobilePreview ? 'on' : ''}`} onClick={() => setShowMobilePreview(!showMobilePreview)}>
        <Icons.Motion sz={22} />
        <span>{showMobilePreview ? 'Close' : 'Preview'}</span>
      </button>

      {/* ── Settings Panel ── */}
      <section className="panel">
        <div className="phead"><Icons.Motion sz={16} /> Motion Sync</div>

        {/* Model Selection */}
        <div className="psec">
          <div className="slbl">Model</div>
          <button className="mp-trigger" onClick={() => setShowModelPicker(true)}>
            <div className="mp-trigger-left">
              <div style={{ display:'flex', alignItems:'center', justifyContent:'center', width: 22, height: 22, marginRight: 10, color: currentModel?.color }}>
                <Icons.Kling sz={20} color={currentModel?.color} />
              </div>
              <div>
                <div className="mp-trigger-name">{currentModel?.label}</div>
              </div>
            </div>
            <div className="mp-trigger-right">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6"/></svg>
            </div>
          </button>
        </div>

        {/* Subject Image Upload */}
        <div className="psec">
          <div className="slbl">Subject Image <span className="opt">— 1 image only</span></div>
          <div className={`uzone ${subjectFile ? 'on' : ''}`} onClick={() => { setLibTarget('image'); setLibOpen(true); }}
            onDragOver={e => e.preventDefault()}
            onDrop={e => {
              e.preventDefault();
              if (e.dataTransfer.files[0]) handleSubjectUpload({ target: { files: e.dataTransfer.files } });
            }}>
            {subjectFile ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ position: 'relative', display: 'inline-block', flexShrink: 0 }}>
                  {subjectPreview && <img src={subjectPreview} alt="" style={{ width: 40, height: 40, borderRadius: 8, objectFit: 'cover' }} />}
                  <button onClick={(e) => { e.stopPropagation(); setSubjectFile(null); setSubjectPreview(null); }} style={{ position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: '50%', background: 'rgba(239, 68, 68, 0.9)', border: 'none', color: '#fff', fontSize: 10, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 5 }}>✕</button>
                </div>
                <div style={{ textAlign: 'left', display: 'flex', flexDirection: 'column' }}>
                  <span style={{ color: 'var(--t2)', fontSize: 12, fontWeight: 700 }}>{subjectFile.name || 'Library Image'}</span>
                </div>
              </div>
            ) : (
              <><div style={{ opacity: .4 }}><Icons.Upload /></div><div>Visual References</div></>
            )}
          </div>
        </div>

        {/* Motion Clip Upload */}
        <div className="psec">
          <div className="slbl">Motion Clip <span className="opt">— MP4/MOV, 100MB max, 3-30s</span></div>
          <div className={`uzone ${motionFile ? 'on' : ''}`} onClick={() => { setLibTarget('video'); setLibOpen(true); }}
            onDragOver={e => e.preventDefault()}
            onDrop={e => {
              e.preventDefault();
              if (e.dataTransfer.files[0]) handleMotionUpload({ target: { files: e.dataTransfer.files } });
            }}>
            {motionFile ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ position: 'relative', display: 'inline-block', flexShrink: 0 }}>
                  <div style={{ width: 40, height: 40, borderRadius: 8, background: 'var(--s3)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--t3)' }}>
                    <span style={{ fontSize: 22 }}>✂️</span>
                  </div>
                  <button onClick={(e) => { e.stopPropagation(); setMotionFile(null); setMotionPreviewUrl(null); }} style={{ position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: '50%', background: 'rgba(239, 68, 68, 0.9)', border: 'none', color: '#fff', fontSize: 10, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 5 }}>✕</button>
                </div>
                <div style={{ textAlign: 'left', display: 'flex', flexDirection: 'column' }}>
                  <span style={{ color: 'var(--t2)', fontSize: 12, fontWeight: 700 }}>{motionFile.name || 'Library Video'}</span>
                  {motionFile.fileUrl ? (
                    <span style={{ color: 'var(--t3)', fontSize: 10 }}>Entire clip (trimming unsupported for library files)</span>
                  ) : (
                    <span style={{ color: '#10b981', fontSize: 10, fontWeight: 600 }}>Trimmed: {motionTrim.start.toFixed(1)}s → {motionTrim.end.toFixed(1)}s ({(motionTrim.end - motionTrim.start).toFixed(1)}s)</span>
                  )}
                </div>
              </div>
            ) : (
              <><div style={{ opacity: .4 }}><Icons.Upload /></div><div>Video References</div></>
            )}
          </div>
          {motionFile && !motionFile.fileUrl && (
            <div style={{ textAlign: 'right', marginTop: 8 }}>
              <span style={{ fontSize: 11, color: 'var(--or)', cursor: 'pointer', fontWeight: 600 }} onClick={() => { setTempFile(motionFile); setShowVideoTrimmer(true); }}>✂️ Adjust Trim</span>
            </div>
          )}
        </div>

        {/* Prompt */}
        <div className="psec">
          <div className="slbl">Motion Prompt <span className="opt">— optional</span></div>
          <div className="pwrap">
            <textarea className="ptxt" style={{ minHeight: 55 }}
              placeholder="Describe the overall context, movement style…"
              value={prompt} onChange={e => setPrompt(e.target.value)} maxLength={2500} />
            <div className="pbot">
              <span className="pcnt">{prompt.length}/2500</span>
            </div>
          </div>
        </div>

        {/* Toggles */}
        <div className="psec">
          <div className="trow">
            <span className="tlbl">🔊 Keep Original Sound</span>
            <div className={`tog ${keepSound ? 'on' : ''}`} onClick={() => setKeepSound(!keepSound)}><div className="togk" /></div>
          </div>
        </div>

        {/* Character Orientation */}
        <div className="psec">
          <div className="slbl">Character Orientation</div>
          <div className="pills">
            <button className={`pill ${orientation === 'exact' ? 'on' : ''}`} onClick={() => setOrientation('exact')}>
              Exact <span style={{ opacity: 0.6, fontSize: 8 }}>(Recommended)</span>
            </button>
            <button className={`pill ${orientation === 'partial' ? 'on' : ''}`} onClick={() => setOrientation('partial')}>
              Partial <span style={{ opacity: 0.6, fontSize: 8 }}>(Max 10s)</span>
            </button>
          </div>
        </div>

        {/* Video Mode */}
        <div className="psec">
          <div className="slbl">Video Mode</div>
          <div className="pills">
            <button className={`pill ${mode === '720p' ? 'on' : ''}`} onClick={() => setMode('720p')}>
              Standard <span style={{ opacity: 0.6, fontSize: 9 }}>(720p)</span>
            </button>
            <button className={`pill ${mode === '1080p' ? 'on' : ''}`} onClick={() => setMode('1080p')}>
              Pro <span style={{ opacity: 0.6, fontSize: 9 }}>(1080p)</span> <span style={{ color: 'var(--or)', fontSize: 9, fontWeight: 800 }}>×1.5 cr</span>
            </button>
          </div>
        </div>

        {/* Generate */}
        <div className="gen-sticky">
          <div className="cost-row">
            <span className="cost-l">Cost</span>
            <span className="cost-v">{cost} cr {' → '}{Math.max(0, (credits || 0) - cost)} left</span>
          </div>
          <button className={`gbtn ${loading ? 'ld' : ''}`} onClick={handleGen} disabled={loading || (user && credits < cost) || cd > 0}>
            {loading ? <><div className="spinner" /> Processing…</> : cd > 0 ? `Wait ${cd}s` : !user ? <>✨ Sign up to Generate</> : <><span style={{ fontSize: 15 }}>🏃</span> Sync Motion</>}
          </button>
          {user && credits < cost && (
            <p style={{ textAlign: 'center', color: 'var(--rd)', fontSize: 11, marginTop: 6, cursor: 'pointer' }} onClick={() => navigate('/subscription')}>Insufficient credits — <u>upgrade</u></p>
          )}
        </div>
      </section>

      {/* ── Main Canvas ── */}
      <div className={`main ${showMobilePreview ? 'mobile-preview-open' : ''}`}>
        <div className="mtbar">
          <button className="mtab main-back-mobile" onClick={() => setShowMobilePreview(false)}>
            <Icons.X sz={13} /> Close
          </button>
          <div className="mtab on">Preview</div>
          <div style={{ flex: 1 }} />
          <div className="mtab" onClick={() => navigate('/gallery')} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <Icons.Grid sz={13} /> Creations
          </div>
        </div>
        <div className="mbody">
          <div className="prev">
            {resultUrl ? (
              <div className="prev-wrap">
                <div className="prev-toolbar">
                  <div className="prev-tb-right" style={{ width: '100%', justifyContent: 'flex-end' }}>
                    <button className="prev-icon-btn" onClick={() => downloadFile(resultUrl, token, true).catch(() => toast.error('Download failed'))} title="Download">
                      <Icons.DL sz={15} />
                    </button>
                    <button className="prev-icon-btn del" onClick={async () => {
                      const isConfirmed = await confirmAlert({
                        title: 'Clear Result?',
                        message: 'Are you sure you want to clear this result?'
                      });
                      if (isConfirmed) setResultUrl(null);
                    }} title="Clear">
                      <Icons.Trash sz={15} />
                    </button>
                  </div>
                </div>
                <video src={resultUrl} controls autoPlay loop style={{ width: '100%', borderRadius: 'var(--r3)', background: '#000' }} />
              </div>
            ) : subjectPreview || motionPreviewUrl ? (
              <div className="prev-wrap" style={{ display: 'flex', flexDirection: 'row', gap: 12, height: 240, alignItems: 'center', justifyContent: 'center', padding: 20, width: '100%', maxWidth: 800 }}>
                {subjectPreview && (
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <div style={{ fontSize: 11, color: 'var(--t3)', marginBottom: 6, fontWeight: 700 }}>SUBJECT</div>
                    <img src={subjectPreview} alt="Subject" style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: 8, background: 'rgba(0,0,0,0.2)', minHeight: 0 }} />
                  </div>
                )}
                {motionPreviewUrl && (
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <div style={{ fontSize: 11, color: 'var(--t3)', marginBottom: 6, fontWeight: 700 }}>
                      TRIMMED CLIP ({motionTrim.start.toFixed(1)}s - {motionTrim.end.toFixed(1)}s = {(motionTrim.end - motionTrim.start).toFixed(1)}s)
                    </div>
                    <video key={motionPreviewUrl} src={motionPreviewUrl} controls muted style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: 8, background: 'rgba(0,0,0,0.2)', minHeight: 0 }} />
                  </div>
                )}
              </div>
            ) : (
              <div className="prev-empty">
                <div className="ico"><Icons.Motion sz={40} /></div>
                <div style={{ fontSize: 13 }}>Motion synced video will appear here</div>
                <div style={{ fontSize: 11, color: 'var(--t3)' }}>
                  Model: <strong style={{ color: currentModel?.color }}>{currentModel?.label}</strong>
                  {' · '}{mode} · {orientation === 'exact' ? 'Exact Orientation' : 'Partial Orientation'}
                </div>
              </div>
            )}
          </div>

          {(localJobs.length > 0 || recentVideos.length > 0) && (
            <>
              <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--t2)', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Icons.Grid sz={13} /> {localJobs.length > 0 ? 'Generating...' : 'Recent Motion Sync'}
              </div>
              <div className="ggrid">
                {localJobs.map(j => (
                  <div key={j.id} className="gitem-pending">
                    <div className="pend-icon"><Icons.Motion sz={28} /></div>
                    <div className="pend-label">{j.prompt || 'Generating...'}</div>
                    <div className="pend-model">{j.model}</div>
                    <div className="pend-bar-wrap"><div className="pend-bar" /></div>
                  </div>
                ))}
              </div>

              <div className="grouped-collections">
                {groupItemsByDate(recentVideos).map(group => (
                  <div key={group.label} className="date-group-wrap">
                    <div className="date-group-label" style={{ marginBottom: 12 }}>{group.label}</div>
                    <div className="ggrid">
                      {group.items.map(i => (
                        <div key={i.id} className="gitem" onClick={() => setLbItem(i)}>
                          {i.videoUrl ? (
                            <video src={i.videoUrl} muted loop playsInline onMouseEnter={e => e.target.play()} onMouseLeave={e => { e.target.pause(); e.target.currentTime = 0; }} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          ) : (
                            <img src={i.imageUrl} alt="" />
                          )}
                          <div className="gbadge" style={{ background: 'rgba(6,182,212,.12)', color: '#06b6d4' }}><Icons.Motion sz={11} /></div>
                          <div className="gact" style={{ position: 'absolute', top: 8, left: 8, display: 'flex', gap: 6, zIndex: 15, opacity: 0, transition: 'opacity 0.2s' }}>
                            <button onClick={(e) => { e.stopPropagation(); import('../utils/downloadMedia').then(m => m.downloadMedia(i.videoUrl || i.imageUrl, i.type, token)); }} style={{ background: 'rgba(0,0,0,0.6)', border: 'none', borderRadius: 6, padding: '5px', cursor: 'pointer', color: '#fff', display: 'flex' }}><Icons.DL sz={14} /></button>
                            <button onClick={(e) => { e.stopPropagation(); if(typeof handleDeleteItem === 'function') handleDeleteItem(i.id || i.$id); }} style={{ background: 'rgba(0,0,0,0.6)', border: 'none', borderRadius: 6, padding: '5px', cursor: 'pointer', color: '#ef4444', display: 'flex' }}><Icons.Trash sz={14} /></button>
                          </div>
                          <div className="gov">
                            <div className="gm">
                              <div style={{ fontWeight: 700, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{i.prompt || 'Motion Sync'}</div>
                              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span>{i.modelName || i.model}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      <Lightbox item={lbItem} onClose={() => setLbItem(null)} onDelete={handleDeleteItem} />
      <MediaLibraryModal
        open={libOpen}
        onClose={() => setLibOpen(false)}
        allowedTypes={libTarget === 'video' ? ['video'] : ['image']}
        maxSelect={1}
        maxTypeCounts={libTarget === 'video' ? { video: 1, image: 0, audio: 0 } : { image: 1, video: 0, audio: 0 }}
        onSelect={(assets) => {
          if (libTarget === 'video') {
            setTempFile(assets[0]);
            setShowVideoTrimmer(true);
          } else {
            setSubjectFile(assets[0]);
            setSubjectPreview(assets[0].fileUrl);
          }
        }}
      />
    </>
  );
}


